import { Log } from './log';

export type ErrorData = {
  /** A well-defined error code such as `user/not-found` */
  code: string;

  /** A message to log to the console. */
  logMessage?: string;

  /** A message for displaying to the user. */
  displayMessage?: string;

  /** The cause of the error. */
  exception?: Error;

  /** Any additional data. */
  extra?: unknown;
};

/**
 * The logger `AppError` reports through.
 *
 * The original imported the application's own logger from `$lib`, which made
 * this file unusable outside one app and gave the two a cycle: `$lib/index.ts`
 * builds the app, the app throws `AppError`, `AppError` imports `$lib`. An app
 * calls `setErrorLogger` during start-up to route these somewhere else.
 */
let logger = new Log();

/** Point `AppError` at an application's logger. */
export function setErrorLogger(next: Log) {
  logger = next;
}

/**
 * The base class for errors in the application.
 *
 * Constructing one logs it. That is the original's behaviour and callers rely
 * on it, so it is preserved — but it does mean an `AppError` that is caught
 * and handled has still been written to the console by the time you see it.
 */
export class AppError extends Error {
  constructor(data: ErrorData) {
    super(data.code);
    this.name = 'AppError';
    this.data = data;
    logger.error(data.code, data.logMessage ?? data.extra, data.exception);
  }
  data: ErrorData;
}
